import { useForm } from 'react-hook-form';
import {
  ButtonWizard,
  LinkWizard,
  LinkWizardSo,
  TextInput,
} from '../../components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import globeImage from '../../assets/images/globe1.png';
import { ArrowLeftIcon, KeySquare } from 'lucide-react';
import {
  useGetUserDetailsQuery,
  useLoginMutation,
  useVerifyingUserMutation,
} from '../../store';
import { useAuth, useNotification } from '../../contexts';
import { extractErrorMessageFromAPI } from '../../utils';
import { encryptPassword } from '../../utils/encryption';
import { useEffect } from 'react';
import PublicAuthLayout from './PublicAuthLayout';
import softgelLogoImg from '../../assets/images/softgel_logo.png';
import softgelBgImg from '../../assets/images/softgel_bg.png';
import denLogo from '../../assets/images/den_logo_image.svg';
import hathLogo from '../../assets/images/hathway_logo.png';
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter';
import { validatePasswordPolicy } from '../../utils/passwordPolicy';
import { saveAssetToken } from '../../services/knowledgeHub';

export interface ISigninForm {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  token: string;
  otp: number;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [verification, { isLoading }] = useVerifyingUserMutation();
  const { data, error, isSuccess } = useGetUserDetailsQuery({ token });
  const { showNotification } = useNotification();
  const [loginUser] = useLoginMutation();
  const { setIsAuthenticated, setUser } = useAuth();
  const navigate = useNavigate();
  const { control, formState, handleSubmit, getValues, setValue, watch } =
    useForm<ISigninForm>({
      defaultValues: {
        password: '',
        confirmPassword: '',
        otp: null,
        token: '',
        name: '',
        email: '',
      },
    });

  useEffect(() => {
    if (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error) ||
          'Failed to load user details. Please try again.',
      );
      console.error(error);
    } else if (isSuccess) {
      setValue('name', data?.data.FullName);
      setValue('email', data?.data.EmailID);
      setValue('token', token);
    }
  }, [isSuccess, error]);

  const clientCode = import.meta.env.VITE_ENV_CLIENT_CODE;
  const isSoftgel = clientCode === 'SOFTGEL';
  const logoSrc = isSoftgel
    ? softgelLogoImg
    : clientCode === 'HATHWAY'
      ? hathLogo
      : denLogo;
  const primaryBtnClass = isSoftgel
    ? 'bg-[#9d0b2d] hover:bg-[#7f0823]'
    : 'bg-blue-600 hover:bg-blue-700';

  const rightInner = (
    <div className="w-full max-w-md space-y-4">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
          Complete your profile
        </h2>
        <p className="text-xs text-gray-600">
          Verify your email and create your password
        </p>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <form className="space-y-2">
          <TextInput
            name={'otp'}
            control={control}
            rules={{
              required: 'Verification code is required',
              minLength: {
                value: 6,
                message: 'Code must be exactly 6 digits',
              },
              maxLength: {
                value: 6,
                message: 'Code must be exactly 6 digits',
              },
              pattern: {
                value: /^\d{6}$/,
                message: 'Code must contain only numbers',
              },
            }}
            label={'Verification Code'}
            variant="text"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              prefix: <KeySquare className="w-4 h-4 text-gray-400" />,
              placeholder: '000000',
              className: 'h-9 text-sm',
            }}
          />

          <TextInput
            name={'name'}
            control={control}
            rules={{
              required: 'Name is required',
              minLength: {
                value: 2,
                message: 'Name must be at least 2 characters',
              },
            }}
            label={'Full Name'}
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              placeholder: 'Enter your full name',
              className: 'h-9 text-sm',
            }}
          />

          <TextInput
            name={'email'}
            control={control}
            label={'Email Address'}
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              disabled: true,
              placeholder: 'you@company.com',
              className: 'h-9 text-sm bg-gray-50',
            }}
          />

          <TextInput
            name={'password'}
            control={control}
            rules={{
              required: 'Password is required',
              validate: value =>
                validatePasswordPolicy(String(value), {
                  email: getValues('email'),
                  fullName: getValues('name'),
                }),
            }}
            label={'Password'}
            variant="password"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              placeholder: 'Create a strong password',
              className: 'h-9 text-sm',
            }}
          />

          <PasswordStrengthMeter
            password={watch('password')}
            context={{
              email: watch('email'),
              fullName: watch('name'),
            }}
          />

          <TextInput
            name={'confirmPassword'}
            control={control}
            rules={{
              required: 'Please confirm your password',
              validate: value =>
                value === getValues('password') || 'Passwords do not match',
            }}
            label={'Confirm Password'}
            variant="password"
            labelClassName="text-xs font-medium text-gray-700"
            formState={formState}
            config={{
              placeholder: 'Confirm your password',
              className: 'h-9 text-sm',
            }}
          />

          <ButtonWizard
            onBtnClick={handleSubmit(handleSubmitForm)}
            addOnClass={`w-full h-9 ${primaryBtnClass} text-white rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md`}
            loading={isLoading}
          >
            Complete Setup
          </ButtonWizard>

          <div className="pt-2 border-t border-gray-200">
            {isSoftgel ? (
              <LinkWizardSo
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-xs font-medium"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizardSo>
            ) : (
              <LinkWizard
                href="/signin"
                addOnClass="flex items-center justify-center gap-2 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to login
              </LinkWizard>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-gray-500">
        &copy; {new Date().getFullYear()}{' '}
        <Link
          target="_blank"
          to="https://sapphiretech.co.in"
          className="text-gray-600 hover:text-blue-600 transition-colors font-medium"
          rel="noopener noreferrer"
        >
          Sapphire Technocrats Pvt Ltd.
        </Link>{' '}
        &mdash; All rights reserved
      </p>
    </div>
  );

  if (isSoftgel) {
    return (
      <div className="h-screen overflow-hidden">
        <PublicAuthLayout
          logoSrc={logoSrc}
          leftBgImageSrc={softgelBgImg}
          accentTextClass="text-teal-500"
        >
          {rightInner}
        </PublicAuthLayout>
      </div>
    );
  }

  async function handleSubmitForm(data: ISigninForm) {
    try {
      const { confirmPassword, ...rest } = data;
      // Encrypt password before sending to backend
      const encryptedPassword = encryptPassword(rest.password);
      const res = await verification({ ...rest, password: encryptedPassword }).unwrap();
      const loginData = {
        email: res?.data?.EmailID,
        password: encryptPassword(confirmPassword),
      };
      const response = await loginUser(loginData).unwrap();
      showNotification('success', 'Success', res?.message);
      sessionStorage.removeItem('redirected');
      setIsAuthenticated(true);
      setUser(response?.data?.user);
      saveAssetToken(response?.data?.assetToken);
      navigate('/');
    } catch (error) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(error) ||
          'Failed to complete setup. Please try again.',
      );
      console.error(error);
    }
  }

  return (
    <div className="h-screen overflow-hidden flex font-sans bg-gray-50">
      {/* Left Side - Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-2/5 relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
        </div>

        {/* Globe Image */}
        <img
          src={globeImage}
          alt="Globe"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-auto opacity-20 pointer-events-none"
          loading="eager"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 py-8 text-white h-full">
          <div className="space-y-6 max-w-lg">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src={logoSrc}
                alt="Client logo"
                className="w-20 h-auto"
                loading="eager"
              />
            </div>

            {/* Title */}
            <div className="space-y-3">
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight">
                Enterprise Portal
              </h1>
              <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full"></div>
              <p className="text-lg text-blue-100 leading-relaxed">
                Account setup
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Onboarding Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-6">
        {rightInner}
      </div>
    </div>
  );
}
